import React, { Component } from "react";
import { compose, withProps } from "recompose";
import {
  withGoogleMap,
  GoogleMap,
  Polygon,
  withScriptjs,
} from "react-google-maps";
import { GOOGLE_MAPS_API_KEY } from "../../../../../constants";
import { Button } from "semantic-ui-react";
import {
  darkTheme,
  lightTheme,
} from "../../../Dashboards/Panel/TrackDevices/MapStyle";
import { toSafeNumber } from "../../../Dashboards/Panel/util";

export interface LatLng {
  lat: number;
  lng: number;
}

function toFiniteCoordinate(value: unknown): number {
  const parsed = toSafeNumber(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeLatLng(point: { lat: unknown; lng: unknown }): LatLng {
  return {
    lat: toFiniteCoordinate(point.lat),
    lng: toFiniteCoordinate(point.lng),
  };
}

function getPathCentroid(path: Array<{ lat: unknown; lng: unknown }>): LatLng | null {
  const finitePoints = path
    .map((point) => ({
      lat: toSafeNumber(point.lat),
      lng: toSafeNumber(point.lng),
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));

  if (finitePoints.length === 0) {
    return null;
  }

  const total = finitePoints.reduce(
    (acc, point) => ({
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng,
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: total.lat / finitePoints.length,
    lng: total.lng / finitePoints.length,
  };
}

interface GeofenceEditorState {
  polygonComplete: boolean;
}

interface GeofenceEditorProps {
  editable: boolean;
  path: LatLng[];
  onPolygonChanged: (path: LatLng[], isComplete: boolean) => void;
  theme: string;
  latitude: number | string;
  longitude: number | string;
  difference?: number;
}

class GeofenceEditor extends Component<
  GeofenceEditorProps,
  GeofenceEditorState
> {
  googleMapRef = React.createRef<GoogleMap>();

  constructor(props: GeofenceEditorProps) {
    super(props);

    this.state = {
      polygonComplete: false,
    };

    this.handleMapClick = this.handleMapClick.bind(this);
    this.handlePolygonComplete = this.handlePolygonComplete.bind(this);
    this.undoLastPoint = this.undoLastPoint.bind(this);
  }

  handleMapClick(event: google.maps.MapMouseEvent) {
    if (!this.state.polygonComplete && event.latLng) {
      const newPath = [
        ...this.props.path,
        { lat: event.latLng.lat(), lng: event.latLng.lng() },
      ];
      this.props.onPolygonChanged(newPath, false);
    }
  }

  handlePolygonComplete() {
    this.setState({
      polygonComplete: this.props.path.length > 2,
    });

    this.props.onPolygonChanged(this.props.path, this.props.path.length > 2);
  }

  undoLastPoint() {
    if (this.props.path?.length > 0) {
      const newPath = this.props.path.slice(0, -1);
      this.setState({
        polygonComplete: false,
      });

      this.props.onPolygonChanged(newPath, false);
    }
  }

  componentDidUpdate(
    prevProps: Readonly<GeofenceEditorProps>,
    prevState: Readonly<GeofenceEditorState>,
    snapshot?: any
  ): void {
    const map = this.googleMapRef.current;
    if (map && this.props.path?.length > 0 && !this.props.editable) {
      const bounds = new google.maps.LatLngBounds();

      this.props.path.forEach((point) => {
        bounds.extend(normalizeLatLng(point));
      });

      map.fitBounds(bounds);
    }
  }

  render() {
    const mapTheme = this.props.theme === "light" ? lightTheme : darkTheme;
    const normalizedPath = this.props.path.map((p) => normalizeLatLng(p));
    const rawCenterLat = toSafeNumber(this.props.latitude);
    const rawCenterLng = toSafeNumber(this.props.longitude);
    const hasFiniteCenter =
      Number.isFinite(rawCenterLat) && Number.isFinite(rawCenterLng);
    const centroid = getPathCentroid(this.props.path);
    const centerLat = hasFiniteCenter
      ? rawCenterLat
      : centroid
      ? centroid.lat
      : 0;
    const centerLng = hasFiniteCenter
      ? rawCenterLng
      : centroid
      ? centroid.lng
      : 0;

    let defaultOptions = {
      streetViewControl: false,
      scaleControl: false,
      mapTypeControl: false,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControlOptions: {},
      panControl: true,
      zoomControl: false,
      rotateControl: true,
      fullscreenControl: false,
      styles: mapTheme,
    };

    return (
      <div style={{ position: "relative" }}>
        {/* @ts-ignore */}
        <GoogleMap
          /**
           * cap zoom values from 5 to 15
           * using tailored m and c values through a linear equation to compute zoom using the difference value caluculated from coordinates
           * not accurate but does the job for most cases - better ux
           * fails to zoom enough for super small geofences
           * fallback to default zoom value which is 8
           */
          zoom={
            this.props.difference
              ? Math.max(
                  5,
                  Math.min(this.props.difference * -2.5129 + 10.9973, 15)
                )
              : 8
          }
          defaultCenter={{
            lat: centerLat,
            lng: centerLng,
          }}
          onClick={this.handleMapClick}
          options={defaultOptions}
          ref={this.googleMapRef}
        >
          <Polygon
            paths={normalizedPath}
            editable={this.props.editable && !this.state.polygonComplete}
            options={{
              strokeColor: "#0000FF",
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: "#0000FF",
              fillOpacity: 0.35,
            }}
            onMouseUp={this.handlePolygonComplete}
          />
        </GoogleMap>

        {this.props.editable ? (
          <Button
            id="undoLastPoint"
            primary
            style={{
              position: "absolute",
              bottom: "15px",
              right: "10px",
            }}
            onClick={this.undoLastPoint}
          >
            Undo Last Point
          </Button>
        ) : (
          <></>
        )}
      </div>
    );
  }
}

const GeofenceEditorWrapper = compose<GeofenceEditorProps, GeofenceEditorProps>(
  withProps({
    googleMapURL:
      "https://maps.googleapis.com/maps/api/js?v=3.exp&key=" +
      GOOGLE_MAPS_API_KEY +
      "&libraries=geometry,drawing,places",
    loadingElement: <div style={{ height: `100%` }} />,
    containerElement: <div style={{ height: `calc(100vh - 392px)` }} />,
    mapElement: <div style={{ height: `100%` }} />,
  }),
  withScriptjs,
  withGoogleMap
)(GeofenceEditor);

export default GeofenceEditorWrapper;
